import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {CircularProgress, TextField} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../Services/ServiceLocator";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {Button} from "@material-ui/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPeopleCarry} from "@fortawesome/free-solid-svg-icons";
import WorkshopTemplate from "./WorkshopTemplate";
import PartDisplay from "../Common/PartDisplay";
import { getDerivedFamilyUnitCount } from '../../helpers/bomFamilyQuantity';
import { groupItemsForBomTable } from '../../helpers/bomLineOrder';

import { AuthenticationService } from "../../Services/AuthenticationService";

// Utility function for formatting decimal quantities
const formatQuantity = (value) => {
    if (value === null || value === undefined) return 1;
    // If it's a whole number, display as integer, otherwise show decimals
    return value % 1 === 0 ? Math.floor(value) : value;
};

const buildAssemblyOption = ({ title, value, description }) => ({
    title,
    value,
    description: description || ''
});

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
});

class BillOfMaterials extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        customDiscount: 50,
        expandedSubAssemblies: new Set(),
        expandedFamilies: new Set(),
        imageModalOpen: false,
        selectedImageUrl: null
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = () => {
        const self = this;

        Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts()]).then(() => {
            self.setState({loading: false});

            let assemblyOptions = [];

            this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((assembly) => {
                assemblyOptions.push(buildAssemblyOption({
                    title: `${assembly.subAssembly.subAssemblyId} rev ${assembly.subAssembly.revision}`,
                    value: assembly.subAssembly.id,
                    description: assembly.subAssembly.description
                }));
            });
            this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
                assemblyOptions.push(buildAssemblyOption({
                    title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`,
                    value: assembly.assembly.id,
                    description: assembly.assembly.description
                }));
            });

            const assemblyId = globalProps.match.params.assemblyId;
            if (assemblyId) self.handleAssemblyChange(null, assemblyOptions.find(x => x.value === assemblyId));
        });
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assemblies) => {
            assemblies = assemblies.sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                return 0;
            });
            self.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssemblies) => {
            subAssemblies = subAssemblies.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                return 0;
            });
            self.setState({subAssemblies});
        });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.sort(function (a, b) {
                if (a.part.partId < b.part.partId) return -1;
                if (a.part.partId > b.part.partId) return 1;
                return 0;
            });
            self.setState({parts});
        });
    };

    handleAssemblyChange = (event, newValue) => {
        this.setState({selectedAssembly: !newValue ? null : newValue.value});
    };

    toggleSubAssemblyExpansion = (subAssemblyId) => {
        const expandedSubAssemblies = new Set(this.state.expandedSubAssemblies);
        if (expandedSubAssemblies.has(subAssemblyId)) {
            expandedSubAssemblies.delete(subAssemblyId);
        } else {
            expandedSubAssemblies.add(subAssemblyId);
        }
        this.setState({ expandedSubAssemblies });
    };

    toggleFamilyExpansion = (familyId) => {
        const expandedFamilies = new Set(this.state.expandedFamilies);
        if (expandedFamilies.has(familyId)) {
            expandedFamilies.delete(familyId);
        } else {
            expandedFamilies.add(familyId);
        }
        this.setState({ expandedFamilies });
    };

    renderBomMemo = (bomNotes) => {
        const text = (bomNotes || '').trim();
        if (!text) return null;

        return (
            <div style={{
                marginTop: 12,
                marginBottom: 12,
                padding: '12px 14px',
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 6,
                textAlign: 'left'
            }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>BOM Memo</div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.45, color: '#333' }}>{text}</div>
            </div>
        );
    };

    openImageModal = (imageUrl) => {
        this.setState({
            imageModalOpen: true,
            selectedImageUrl: imageUrl
        });
    };

    closeImageModal = () => {
        this.setState({
            imageModalOpen: false,
            selectedImageUrl: null
        });
    };

    getFirstImageUrl = (imageURLs) => {
        if (!imageURLs || imageURLs.trim() === '') return null;
        const urls = imageURLs.split('~|~');
        return urls[0] || null;
    };

    groupPartsByFamily = (partsToAssemblies) => {
        const families = {};
        const individualParts = [];

        partsToAssemblies.forEach((partToAssembly) => {
            if (partToAssembly.partFamilyId) {
                if (!families[partToAssembly.partFamilyId]) {
                    families[partToAssembly.partFamilyId] = {
                        familyId: partToAssembly.partFamilyId,
                        familyName: partToAssembly.partFamilyName,
                        familyDescription: partToAssembly.partFamilyDescription,
                        familyImageURL: partToAssembly.partFamilyImageURL,
                        parts: []
                    };
                }
                families[partToAssembly.partFamilyId].parts.push(partToAssembly);
            } else {
                individualParts.push(partToAssembly);
            }
        });

        // Sort parts within each family by order
        Object.values(families).forEach(family => {
            family.parts.sort((a, b) => (a.order || 0) - (b.order || 0));
        });

        return {
            families: Object.values(families),
            individualParts
        };
    };

    renderSubAssemblyParts = (subAssembly, depth = 1) => {
        // Single ordered list across child subassemblies, parts, and families (matches Edit Assembly)
        const bomLines = groupItemsForBomTable(
            [],
            subAssembly.partsToSubAssemblies,
            subAssembly.childSubAssemblies || []
        );
        const partsHtml = [];

        bomLines.forEach((bomItem) => {
            if (bomItem.itemType === 'subassembly') {
                const childSubAssemblyToSubAssembly = bomItem;
                const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyToSubAssembly.childSubAssemblyFk);
                if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                const childSubAssembly = childSubAssemblyExpanded.subAssembly;
                const isExpanded = this.state.expandedSubAssemblies.has(childSubAssembly.id);

                const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
                const backgroundColor = depth % 2 === 1 ? '#e6f3ff' : '#f0f8ff'; // Alternate colors for depth

                const childSubAssemblyRow = (
                    <tr key={`sub-${subAssembly.id}-child-${childSubAssembly.id}`} style={{backgroundColor}}>
                        <td style={{textAlign: 'center', padding: '4px'}}>
                            <span 
                                onClick={() => this.toggleSubAssemblyExpansion(childSubAssembly.id)}
                                style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                            >
                                {isExpanded ? '▼' : '▶'}
                            </span>
                        </td>
                        <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                            <PartDisplay 
                                part={{
                                    partId: childSubAssembly.subAssemblyId,
                                    revision: childSubAssembly.revision,
                                    description: childSubAssembly.description
                                }} 
                                variant="table" 
                                classes={this.props.classes} 
                            />
                        </td>
                        <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(childSubAssemblyToSubAssembly.quantity)}</td>
                    </tr>
                );

                partsHtml.push(childSubAssemblyRow);

                // Recursively render the child subassembly's parts and children if expanded
                if (isExpanded) {
                    const childParts = this.renderSubAssemblyParts(childSubAssembly, depth + 1);
                    partsHtml.push(...childParts);
                }
            } else if (bomItem.itemType === 'part') {
            const subAssemblyPart = bomItem;
            const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
            if (!expandedPart || expandedPart.part.isActive === false) return;
            const part = expandedPart.part;

            const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
            partsHtml.push(
                <tr key={`sub-${subAssembly.id}-part-${part.id}`} style={{backgroundColor: '#dbdbdb'}}>
                    <td></td>
                    <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                        <PartDisplay part={part} variant="table" classes={this.props.classes} />
                    </td>
                    <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyPart.quantity)}</td>
                </tr>
            );
            } else if (bomItem.itemType === 'family') {
            const family = bomItem;
            const subFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);
            // Family header row
            partsHtml.push(
                <tr key={`sub-${subAssembly.id}-family-${family.familyId}`} 
                    style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                    <td style={{textAlign: 'center', padding: '4px'}}>
                        <span 
                            onClick={() => this.toggleFamilyExpansion(family.familyId)}
                            style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                        >
                            {this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId) ? '▼' : '▶'}
                        </span>
                    </td>
                    <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                        <div style={{display: 'flex', alignItems: 'baseline'}}>
                            <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}><FontAwesomeIcon icon={faPeopleCarry} style={{ marginRight: '6px' }} />{family.familyName}</span>
                        </div>
                        {family.familyDescription && (
                            <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                {family.familyDescription}
                            </div>
                        )}
                    </td>
                    <td style={{width: '60px', textAlign: 'center'}}>{subFamilyUnitCount != null ? formatQuantity(subFamilyUnitCount) : ''}</td>
                </tr>
            );

            // Family parts - only show if expanded
            if (this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId)) {
                family.parts.forEach((subAssemblyPart) => {
                const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
                if (!expandedPart || expandedPart.part.isActive === false) return;
                const part = expandedPart.part;

                const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
                partsHtml.push(
                    <tr key={`sub-${subAssembly.id}-family-${family.familyId}-part-${part.id}`} 
                        style={{backgroundColor: '#f0f8ff'}}>
                        <td></td>
                        <td style={{paddingLeft: `${paddingLeft}px`}} className="text-left">
                            <PartDisplay part={part} variant="table" classes={this.props.classes} />
                        </td>
                        <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyPart.quantity)}</td>
                    </tr>
                );
            });
            }
            }
        });

        return partsHtml;
    };

    render() {
        if (this.state.loading) {
            return (
                <WorkshopTemplate>
                    <div className="mobile-container">
                        <CircularProgress/>
                    </div>
                </WorkshopTemplate>
            );
        }

        let assemblyOptions = [];

        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((assembly) => {
            assemblyOptions.push(buildAssemblyOption({
                title: `${assembly.subAssembly.subAssemblyId} rev ${assembly.subAssembly.revision}`,
                value: assembly.subAssembly.id,
                description: assembly.subAssembly.description
            }));
        });

        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            assemblyOptions.push(buildAssemblyOption({
                title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`,
                value: assembly.assembly.id,
                description: assembly.assembly.description
            }));
        });

        let assemblyListHtml = [];
        if (this.state.selectedAssembly) {
            let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly);
            let assemblyMaybe = assemblyExpandedMaybe ? this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly).assembly : null;
            let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly);
            let subAssemblyMaybe = !assemblyMaybe && subAssemblyExpandedMaybe ? this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly).subAssembly : null;

            if (assemblyMaybe) {
                const bomLines = groupItemsForBomTable(
                    assemblyMaybe.subAssembliesToAssemblies,
                    assemblyMaybe.partsToAssemblies
                );
                const rowsHtml = [];

                bomLines.forEach((bomItem) => {
                    if (bomItem.itemType === 'subassembly') {
                    const subAssemblyToAssembly = bomItem;
                    const subAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === subAssemblyToAssembly.subAssemblyFk);
                    if (!subAssemblyExpanded || !subAssemblyExpanded.subAssembly.isActive) return;
                    const subAssembly = subAssemblyExpanded.subAssembly;
                    const isExpanded = this.state.expandedSubAssemblies.has(subAssembly.id);

                    const subAssemblyRow = (
                        <tr key={`subassembly-${subAssembly.id}`}>
                            <td style={{textAlign: 'center', padding: '4px'}}>
                                <span 
                                    onClick={() => this.toggleSubAssemblyExpansion(subAssembly.id)}
                                    style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                >
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            </td>
                            <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                <PartDisplay 
                                    part={{
                                        partId: subAssembly.subAssemblyId,
                                        revision: subAssembly.revision,
                                        description: subAssembly.description
                                    }} 
                                    variant="table" 
                                    classes={this.props.classes} 
                                />
                            </td>
                            <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyToAssembly.quantity)}</td>
                        </tr>
                    );

                    const subAssemblyParts = isExpanded ? this.renderSubAssemblyParts(subAssembly) : [];

                    rowsHtml.push(subAssemblyRow, ...subAssemblyParts);
                    } else if (bomItem.itemType === 'part') {
                        const assemblyPart = bomItem;
                        const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                        if (!expandedPart || expandedPart.part.isActive === false) return;
                        const part = expandedPart.part;

                        rowsHtml.push(
                            <tr key={`assembly-part-${part.id}`}>
                                <td></td>
                                <td className="text-left">
                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                            </tr>
                        );
                    } else if (bomItem.itemType === 'family') {
                        const family = bomItem;
                        const assemblyFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);
                        // Family header row
                        rowsHtml.push(
                            <tr key={`assembly-family-${family.familyId}`} 
                                style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleFamilyExpansion(family.familyId)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId) ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}><FontAwesomeIcon icon={faPeopleCarry} style={{ marginRight: '6px' }} />{family.familyName}</span>
                                    </div>
                                    {family.familyDescription && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {family.familyDescription}
                                        </div>
                                    )}
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{assemblyFamilyUnitCount != null ? formatQuantity(assemblyFamilyUnitCount) : ''}</td>
                            </tr>
                        );

                        // Family parts - only show if expanded
                        if (this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId)) {
                            family.parts.forEach((assemblyPart) => {
                            const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                            if (!expandedPart || expandedPart.part.isActive === false) return;
                            const part = expandedPart.part;

                            rowsHtml.push(
                                <tr key={`assembly-family-${family.familyId}-part-${part.id}`} 
                                    style={{backgroundColor: '#f0f8ff'}}>
                                    <td></td>
                                    <td className="text-left" style={{paddingLeft: '15px'}}>
                                        <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                    </td>
                                    <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                </tr>
                            );
                        });
                        }
                    }
                });

                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{assemblyMaybe.assemblyId} rev {assemblyMaybe.revision}</h3>
                        {this.renderBomMemo(assemblyMaybe.bomNotes)}
                        <div className="parts-table-container">
                            <table className="parts-table" style={{minWidth: '100%'}}>
                                <thead>
                                <tr>
                                    <th style={{width: '30px'}}></th>
                                    <th style={{width: 'auto', minWidth: '200px'}}>Part Number + Description</th>
                                    <th style={{width: '60px'}}>Qty</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rowsHtml}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            } else {
                // Single ordered list across child subassemblies, parts, and families (matches Edit Assembly)
                const bomLines = groupItemsForBomTable(
                    [],
                    subAssemblyMaybe.partsToSubAssemblies,
                    subAssemblyMaybe.childSubAssemblies || []
                );
                const rowsHtml = [];

                bomLines.forEach((bomItem) => {
                    if (bomItem.itemType === 'subassembly') {
                        const childSubAssemblyToSubAssembly = bomItem;
                        const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyToSubAssembly.childSubAssemblyFk);
                        if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                        const childSubAssembly = childSubAssemblyExpanded.subAssembly;
                        const isExpanded = this.state.expandedSubAssemblies.has(childSubAssembly.id);

                        const childSubAssemblyRow = (
                            <tr key={`child-subassembly-${childSubAssembly.id}`}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleSubAssemblyExpansion(childSubAssembly.id)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {isExpanded ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <PartDisplay 
                                        part={{
                                            partId: childSubAssembly.subAssemblyId,
                                            revision: childSubAssembly.revision,
                                            description: childSubAssembly.description
                                        }} 
                                        variant="table" 
                                        classes={this.props.classes} 
                                    />
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(childSubAssemblyToSubAssembly.quantity)}</td>
                            </tr>
                        );

                        const childSubAssemblyParts = isExpanded ? this.renderSubAssemblyParts(childSubAssembly) : [];

                        rowsHtml.push(childSubAssemblyRow, ...childSubAssemblyParts);
                    } else if (bomItem.itemType === 'part') {
                        const assemblyPart = bomItem;
                        const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                        if (!expandedPart || expandedPart.part.isActive === false) return;
                        const part = expandedPart.part;

                        rowsHtml.push(
                            <tr key={`subassembly-part-${part.id}`}>
                                <td></td>
                                <td className="text-left">
                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                            </tr>
                        );
                    } else if (bomItem.itemType === 'family') {
                        const family = bomItem;
                        const standaloneSubFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);
                        // Family header row
                        rowsHtml.push(
                            <tr key={`subassembly-family-${family.familyId}`} 
                                style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleFamilyExpansion(family.familyId)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId) ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}><FontAwesomeIcon icon={faPeopleCarry} style={{ marginRight: '6px' }} />{family.familyName}</span>
                                    </div>
                                    {family.familyDescription && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {family.familyDescription}
                                        </div>
                                    )}
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{standaloneSubFamilyUnitCount != null ? formatQuantity(standaloneSubFamilyUnitCount) : ''}</td>
                            </tr>
                        );

                        // Family parts - only show if expanded
                        if (this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId)) {
                            family.parts.forEach((assemblyPart) => {
                            const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                            if (!expandedPart || expandedPart.part.isActive === false) return;
                            const part = expandedPart.part;

                            rowsHtml.push(
                                <tr key={`subassembly-family-${family.familyId}-part-${part.id}`} 
                                    style={{backgroundColor: '#f0f8ff'}}>
                                    <td></td>
                                    <td className="text-left" style={{paddingLeft: '15px'}}>
                                        <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                    </td>
                                    <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                </tr>
                            );
                        });
                        }
                    }
                });

                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        <h3>{subAssemblyMaybe.subAssemblyId} rev {subAssemblyMaybe.revision}</h3>
                        {this.renderBomMemo(subAssemblyMaybe.bomNotes)}
                        <div className="parts-table-container">
                            <table className="parts-table" style={{minWidth: '100%'}}>
                                <thead>
                                <tr>
                                    <th style={{width: '30px'}}></th>
                                    <th style={{width: 'auto', minWidth: '200px'}}>Part Number + Description</th>
                                    <th style={{width: '60px'}}>Qty</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rowsHtml}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }
        }

        const assemblyId = globalProps.match.params.assemblyId;

        return (
            <WorkshopTemplate>
                <div className="mobile-container" style={{maxWidth: 1000}}>
                    {assemblyId ? <div>
                        <Button onClick={() => globalProps.history.push(`/search/${assemblyId}`)}
                                      className="success-button-lg">Assembly Home</Button>
                    </div> : ""}
                    <h1>Bill Of Materials</h1>
                    <Autocomplete autoHighlight
                        id="ddlAssembly"
                        options={assemblyOptions}
                        onChange={this.handleAssemblyChange}
                        value={this.state.selectedAssembly}
                        getOptionLabel={(option) => option.title}
                        getOptionSelected={(option) => option.value === this.state.selectedAssembly}
                        renderOption={(option) => (
                            <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                <span style={{fontWeight: 600, color: '#222'}}>{option.title}</span>
                                {option.description ? <span style={{fontSize: '12px', color: '#666'}}>{option.description}</span> : null}
                            </div>
                        )}
                        className="autocomplete-dropdown"
                        style={{maxWidth: 600}}
                        renderInput={(params) => <TextField {...params} inputProps={{
                            ...params.inputProps
                        }} label="Assembly" variant="outlined" autoComplete="new-password"/>}
                    />
                    {assemblyListHtml}
                </div>
            </WorkshopTemplate>
        );
    }
}

let globalProps;

BillOfMaterials.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(BillOfMaterials);
